import React, { useCallback, useMemo, useState } from "react"
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@material-ui/core";
import clsx from "clsx";
import { FormattedMessage } from "react-intl";
import { FormControl, RadioGroup, Radio } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { useProductFormStyles } from "./ProductForm.style";
import { VisibilityType } from "./ProductForm.type";
import { updateFormField } from "../../../../../store/actions/productFormActions";
import { RootState } from "../../../../../store/types";
import { useAuthorization } from "pages/home/authorizer/components/AuthorizationProvider";

interface VisibilityOption {
  value: VisibilityType;
  labelKey: string;
  descriptionKey: string;
  badgeClass?: string;
  disabled?: boolean;
}

interface ProductVisibilityProps {
  /** Create: Public is never shown. Edit: Public when authorize response includes make_public for this PRODUCT. */
  isEditMode?: boolean;
  /** When user confirms making product public, called to update form and trigger product PUT. */
  onMakePublicConfirm?: () => Promise<void>;
}

const ALL_VISIBILITY_OPTIONS: VisibilityOption[] = [
  { value: 'team', labelKey: 'My Team', descriptionKey: 'Visible only to members of your team.' },
  { value: 'account', labelKey: 'My Account', descriptionKey: 'Visible to all members within your account.' },
  { value: 'organization', labelKey: 'Organization', descriptionKey: 'Visible to everyone within your organization.' },
  { value: 'public', labelKey: 'Public', descriptionKey: 'Visible to all.' },
];

export const ProductVisibility: React.FC<ProductVisibilityProps> = ({ isEditMode = false, onMakePublicConfirm }) => {
    const classes = useProductFormStyles();
    const dispatch = useDispatch();
    const [makePublicDialogOpen, setMakePublicDialogOpen] = useState(false);
    const [makePublicSubmitting, setMakePublicSubmitting] = useState(false);

    const { visibility, product } = useSelector((state: RootState) => state.productForm);
    /** PRODUCT resource auth (e.g. allowedActions includes make_public) — populated by AuthorizationProvider on product details. */
    const productId = product?.id ?? "";
    const { isActionAllowed } = useAuthorization("PRODUCT", productId);
    /** Saved visibility from backend — lock UI only when persisted as public, not when user is drafting a change. */
    const persistedVisibility = (product?.visibility ?? '').toLowerCase();
    const isPublicPersisted = persistedVisibility === 'public';
    /** When product is already public on the server, no role may change visibility; all radios disabled. */
    const isVisibilityLocked = isEditMode && isPublicPersisted;
    /** Edit only: show Public option and "make public" flow when authorize response includes make_public for this PRODUCT. */
    const canMakePublicProduct = isEditMode && isActionAllowed("make_public");

    const visibilityOptions: VisibilityOption[] = useMemo(() => {
      // Create: never show Public (team / account / organization only)
      if (!isEditMode) {
        return ALL_VISIBILITY_OPTIONS.filter((o) => o.value !== 'public');
      }

      // Edit + product is Public: show all four so everyone sees Public selected; entire section disabled
      if (isVisibilityLocked) {
        return ALL_VISIBILITY_OPTIONS.map((o) => ({ ...o, disabled: true }));
      }

      // Edit + product is not Public + user has make_public on PRODUCT: all four options
      if (canMakePublicProduct) {
        return ALL_VISIBILITY_OPTIONS.map((o) => ({ ...o, disabled: false }));
      }

      // Edit + no make_public: only the three non-public options
      return ALL_VISIBILITY_OPTIONS
        .filter((o) => o.value !== 'public')
        .map((o) => ({ ...o, disabled: false }));
    }, [isEditMode, isVisibilityLocked, canMakePublicProduct]);

    const handleVisibilityChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      if (isVisibilityLocked) return;
      const value = e.target.value as VisibilityType;
      if (value === 'public' && isEditMode && canMakePublicProduct && onMakePublicConfirm) {
        setMakePublicDialogOpen(true);
        return;
      }
      dispatch(updateFormField('visibility', value, true, null));
      if (value === 'public') {
        dispatch(updateFormField('approvalFlow', 'open', true, null));
      }
    }, [dispatch, isEditMode, canMakePublicProduct, onMakePublicConfirm, isVisibilityLocked]);

    const handleMakePublicCancel = useCallback(() => {
      setMakePublicDialogOpen(false);
    }, []);

    const handleMakePublicConfirm = useCallback(async () => {
      if (!onMakePublicConfirm) return;
      setMakePublicSubmitting(true);
      try {
        await onMakePublicConfirm();
        setMakePublicDialogOpen(false);
      } finally {
        setMakePublicSubmitting(false);
      }
    }, [onMakePublicConfirm]);
    return (
        <>
         {/* Product Visibility Section */}
         <Box>
         <Typography className={classes.sectionTitle}>
           <FormattedMessage defaultMessage="Product Visibility" />
         </Typography>
         <FormControl
           component="fieldset"
           className={clsx(isVisibilityLocked && classes.visibilityRadiosPublicLocked)}
         >
           <RadioGroup
             value={visibility.value}
             onChange={handleVisibilityChange}
             className={classes.radioGroup}
           >
            {visibilityOptions.map((option) => (
               <Box key={option.value} className={classes.radioOption}>
                 <Box className={classes.radioContent}>
                   <Radio
                     value={option.value}
                     color="primary"
                     className={classes.radioControl}
                     disabled={option.disabled}
                   />
                   <Box className={classes.radioText}>
                     <Typography
                       className={clsx(
                         classes.radioLabel,
                         isVisibilityLocked && classes.radioLabelLockedPublic
                       )}
                     >
                       {option.labelKey}
                     </Typography>
                     <Typography
                       className={clsx(
                         classes.radioDescription,
                         isVisibilityLocked && classes.radioDescriptionLockedPublic
                       )}
                     >
                       {option.descriptionKey}
                     </Typography>
                   </Box>
                 </Box>
               </Box>
             ))}
           </RadioGroup>
         </FormControl>
       </Box>

       <Dialog
         open={makePublicDialogOpen}
         onClose={handleMakePublicCancel}
         maxWidth="sm"
         fullWidth
         disableBackdropClick={makePublicSubmitting}
         disableEscapeKeyDown={makePublicSubmitting}
         PaperProps={{ className: classes.makePublicDialogPaper }}
       >
         <DialogTitle disableTypography className={classes.makePublicDialogTitleRoot}>
           <Typography component="h2" variant="h6" className={classes.makePublicDialogTitleText}>
             <FormattedMessage defaultMessage="Make this product public?" />
           </Typography>
         </DialogTitle>
         <DialogContent className={classes.makePublicDialogContent}>
           <Typography component="div" className={classes.makePublicDialogBody}>
             <FormattedMessage
               defaultMessage="You are about to make this product visible to all external developers. This will automatically set the subscription approval to {openLabel}."
               values={{ openLabel: <strong>Open</strong> }}
             />
           </Typography>
         </DialogContent>
         <DialogActions className={classes.makePublicDialogActions}>
           <Button
             onClick={handleMakePublicCancel}
             variant="contained"
             disableElevation
             className={classes.makePublicDialogCancelButton}
           >
             <FormattedMessage defaultMessage="Cancel" />
           </Button>
           <Button
             onClick={handleMakePublicConfirm}
             variant="contained"
             disableElevation
             disabled={makePublicSubmitting}
             className={classes.makePublicDialogConfirmButton}
           >
             {makePublicSubmitting ? (
               <FormattedMessage defaultMessage="Updating…" />
             ) : (
               <FormattedMessage defaultMessage="Make public" />
             )}
           </Button>
         </DialogActions>
       </Dialog>
       </>
    );
}
